import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BellRing, CalendarDays, Home, MessageCircle, Wallet } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(n || 0);
const fmt = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("tr-TR") : "—");
const errText = (e) => { const d = e.response?.data?.detail; return Array.isArray(d) ? "Bilgileri kontrol edin" : d || "İşlem başarısız"; };
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

function Metrics({ rows }) {
  const active = rows.filter((r) => r.status === "completed");
  const soon = active.filter((r) => r.rent_remind && r.next_reminder && (new Date(`${r.next_reminder}T00:00:00`) - new Date()) / 864e5 <= 7).length;
  const items = [["Teslim edilen kira", active.length, Home], ["Hatırlatması açık", active.filter((r) => r.rent_remind).length, BellRing], ["Aylık toplam kira", money(active.reduce((s, r) => s + r.amount, 0)), Wallet], ["7 gün içinde hatırlatma", soon, CalendarDays]];
  return (
    <div className="metric-grid rent-metrics" data-testid="rent-metrics">
      {items.map(([label, value, Icon], i) => <div className="metric" key={label} data-testid={`rent-metric-${i}`}><div className="metric-top"><span>{label}</span><Icon size={18} /></div><strong>{value}</strong></div>)}
    </div>
  );
}

function RentRow({ r, onSaved }) {
  const [busy, setBusy] = useState(false);
  const save = async (patch) => {
    setBusy(true);
    try { const res = await axios.put(`${API}/rentals/${r.id}`, { rent_day: r.rent_day, rent_remind: r.rent_remind, ...patch }); onSaved(res.data); toast.success("Kira hatırlatması güncellendi"); }
    catch (e) { toast.error(errText(e)); }
    setBusy(false);
  };
  const pending = r.status !== "completed";
  return (
    <div className="rent-row" data-testid={`rent-row-${r.code || r.id}`}>
      <span className="contract-meta"><b>{r.code || "Kodsuz"}</b><small>{r.listing || "—"}</small></span>
      <span className="contract-meta"><b>{r.tenant_name || "—"}</b><small className={r.phone_ok ? "" : "expense-text"}>{r.tenant_phone || "Telefon yok"}</small></span>
      <span className="contract-meta"><b>{r.landlord_name || "—"}</b><small>{r.landlord_phone || ""}</small></span>
      <strong>{money(r.amount)}</strong>
      <label className="rent-day">Her ayın
        <select value={r.rent_day || ""} disabled={busy} onChange={(e) => save({ rent_day: e.target.value ? Number(e.target.value) : null, rent_remind: e.target.value ? r.rent_remind : false })} data-testid={`rent-day-select-${r.code || r.id}`}>
          <option value="">—</option>{DAYS.map((d) => <option key={d} value={d}>{d}'i</option>)}
        </select>
      </label>
      <span className="contract-meta" data-testid={`rent-next-${r.code || r.id}`}>
        {pending ? <><b>Teslim bekleniyor</b><small>Hatırlatma teslimden sonra başlar</small></> : r.next_due ? <><b>{fmt(r.next_due)}</b><small>{r.rent_remind ? `Hatırlatma: ${fmt(r.next_reminder)} 12:00` : "Hatırlatma kapalı"}</small></> : <><b>—</b><small>Ödeme günü seçilmedi</small></>}
        {r.last_error && r.rent_remind && <small className="expense-text" data-testid={`rent-error-${r.code || r.id}`}>Son gönderim başarısız</small>}
      </span>
      <span className="rent-switch"><Switch checked={r.rent_remind} disabled={busy} onCheckedChange={(v) => save({ rent_remind: v })} data-testid={`rent-remind-switch-${r.code || r.id}`} /><small>{r.rent_remind ? "Açık" : "Kapalı"}</small></span>
    </div>
  );
}

export default function RentTracking() {
  const [rows, setRows] = useState(null), [tplOk, setTplOk] = useState(null);
  useEffect(() => {
    axios.get(`${API}/rentals`).then((r) => setRows(r.data)).catch(() => toast.error("Kira listesi yüklenemedi"));
    axios.get(`${API}/whatsapp/status`).then((r) => setTplOk(!!r.data.templates?.rent)).catch(() => {});
  }, []);
  const update = (row) => setRows((p) => p.map((x) => (x.id === row.id ? row : x)));
  return (
    <>
      <div className="page-title"><div><span className="eyebrow">EMLAK SEPETİ</span><h1 data-testid="page-title">Kira Takibi</h1><p>Kiracılarınızın ödeme günleri. Hatırlatması açık olan kiracıya ödeme gününden 3 gün önce saat 12:00'de WhatsApp mesajı gider.</p></div></div>
      {tplOk === false && <div className="rent-banner" data-testid="rent-template-banner"><MessageCircle size={16} />Kira hatırlatma şablonu henüz bağlanmadı. Twilio onayından sonra mesajlar otomatik gitmeye başlar.</div>}
      {!rows ? <p className="empty" data-testid="rent-loading">Yükleniyor…</p> : (
        <>
          <Metrics rows={rows} />
          <section className="panel" data-testid="rent-list">
            <div className="rent-row head"><span>SÖZLEŞME</span><span>KİRACI</span><span>EV SAHİBİ</span><span>KİRA</span><span>ÖDEME GÜNÜ</span><span>SONRAKİ ÖDEME</span><span>HATIRLATMA</span></div>
            {rows.length === 0 && <p className="empty" data-testid="rent-empty">Henüz kira sözleşmesi yok. Sözleşmeler sayfasından kira sözleşmesi ekleyin.</p>}
            {rows.map((r) => <RentRow key={r.id} r={r} onSaved={update} />)}
          </section>
        </>
      )}
    </>
  );
}
